import { useRef } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { useParams } from 'react-router-dom';
import { motion, useInView } from "framer-motion";

import { storyData } from '../../compontents/story/StoryData';
import { normalize } from '../../utils/Utilities';
import FormatText from '../../utils/formatting/TextFormatter';
import { useNavigation } from '../../hooks/useNavigation';

import StoryImageLoader from './StoryImageLoader';
import ErrorPage from '../error/ErrorPage';

export default function StoryPage() {
    const { title } = useParams();
    const story = storyData.find((s) => normalize(s.title) === title);

    const { goBlogStories } = useNavigation();

    if (!story) {
        return (
            <ErrorPage />
        );
    }

    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: '-100px' });

    let filteredStories = storyData.filter((s) => s.title !== story.title);

    return (
        <Box
            sx={{
                position: 'relative',
                backgroundColor: "#111111",
                paddingTop: "150px",
                paddingBottom: "80px",
                paddingX: { xs: 2, sm: 5, md: 0 },
                width: "100%",
                height: "100%",
                minHeight: "100vh",
                display: "flex",
                justifyContent: "center",
                overflow: "visible",
                pb: '75px',
                zIndex: 3
            }}
        >  
            <motion.div
                ref={ref}
                initial={{ opacity: 0, y: 40 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
            >
                <Box sx={{ maxWidth: { sm: 'auto', md: '730px', lg: '1208px' }, width: '100%' }}>
                    <Stack
                        spacing={2.5} 
                        alignItems="center"
                        sx={{
                            maxWidth: 1208,
                            width: "100%",
                            flexFlow: "column",
                            overflow: "visible",
                            position: "relative",
                            zIndex: 2,
                            padding: "50px 0 80px",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexFlow: "column",
                                alignItems: "center",
                                gap: "20px",
                                width: "100%",
                                overflow: "visible",
                                padding: "3px 5px",
                                position: "relative",
                                color: "gray",
                                fontSize: "1rem",
                                lineHeight: 1.8,
                            }}
                        >
                            {story.date}
                        </Box>

                        <Typography
                            sx={{
                                fontSize: { xs: "36px", sm: "43px", md: "51px", lg: "64px" }, 
                                color: "white",
                                textAlign: "center",
                                paddingBottom: "80px",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                maxWidth: 800,
                                width: "100%",
                                lineHeight: 1.3,
                                position: "relative",
                                margin: 0
                            }}
                        >
                            {story.title}
                        </Typography>

                        <Box
                            sx={{
                                backgroundColor: "rgb(195, 219, 98)",
                                px: { xs: 2, sm: 2, md: 3 },
                                py: { xs: 0.5, sm: 1 },
                                borderRadius: 2,
                                width: "fit-content",
                                maxWidth: "100%",
                                overflowWrap: "break-word",
                            }}
                            >
                            <Typography
                                sx={{
                                    fontSize: { xs: "14px", sm: "16px" },
                                    color: "black",
                                    lineHeight: 1.3,
                                    wordBreak: "break-word",
                                }}
                            >
                                {story.type}
                            </Typography>
                        </Box>
                    </Stack>

                   <StoryImageLoader src={story.image} alt="" />

                    <Box
                        sx={{
                            maxWidth: { sm: 'fit-content', md: '730px', lg: '730px' },
                            width: '100%',
                            marginX: 'auto',
                            paddingX: { xs: 2, sm: 5, md: 0 }
                        }}
                    >
                        <FormatText 
                            key={title} 
                            sections={story.content} 
                            url={title} 
                        />

                        <Box sx={{ width: '100%' }}>
                            <Box sx={{ borderBottom:  '1px solid #303030'}}>
                                <Typography sx={{ fontSize: '16px', color: 'white', mb: 1, mt: 4, padding: '0px 0px 16px', textAlign: 'center' }}>
                                    MY RECENT STORIES
                                </Typography>
                            </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        gap: { xs: 2, sm: 4 },
                                        justifyContent: 'center',
                                        alignItems: 'stretch',
                                        mt: 2,
                                    
                                    }}
                                >
                                    {filteredStories.slice(0, 2).map((s, i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                flex: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                fontWeight: 500,
                                                fontSize: 15,
                                                pt: '38px',
                                                pb: '38px',
                                                px: { xs: '0px', sm: '20px' },
                                                position: 'relative',
                                                overflow: 'hidden',
                                                width: 'fit-content',
                                                gap: '18px',
                                            }}
                                            onClick={() => goBlogStories(normalize(s.title))}
                                        >
                                            <Box
                                                sx={{
                                                    background: "rgb(195, 219, 98)",
                                                    padding: "6px 16px",
                                                    borderRadius: 2,
                                                    transition: "background-color 0.3s ease",
                                                    whiteSpace: "nowrap",
                                                    width: 'fit-content',
                                                    mb: 2,
                                                }}
                                            >
                                                <Typography sx={{ fontSize: "16px", color: "black" }}>
                                                    {s.type}
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ fontSize: '16px', color: 'gray', mb: 1 }}>
                                                {s.date}
                                            </Typography>
                                            <Typography 
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        goBlogStories(normalize(s.title));
                                                    }
                                                }}
                                                role="button"
                                                tabIndex={0}
                                                aria-label={`Open story: ${s.title}`}
                                                sx={{ 
                                                    cursor: 'pointer', 
                                                    fontSize: { xs: '20px',sm: '20px', md:'24px'},
                                                    color: 'white', 
                                                    mt: 1,
                                                    textAlign: 'left',
                                                    wordBreak: 'break-word',
                                                    '&:hover': { color: 'rgb(195, 219, 98)' }
                                                }}
                                                className='tab-indictor'
                                            >
                                                {s.title}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                        </Box>
                    </Box>
                </Box>
            </motion.div>
        </Box>
    );
}
